import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-foreground px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.4em] uppercase text-[#C8A96A] mb-4">Admin Access</p>
          <h1 className="font-serif text-4xl font-medium text-background">Sign In</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs tracking-widest uppercase text-background/50 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border border-background/20 text-background px-4 py-3 text-sm focus:border-[#C8A96A] focus:outline-none transition-colors"
              placeholder="admin@musesocialcreative.com"
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase text-background/50 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border border-background/20 text-background px-4 py-3 text-sm focus:border-[#C8A96A] focus:outline-none transition-colors"
              placeholder="Enter password"
              data-testid="login-password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#C8A96A] text-foreground font-medium text-sm tracking-widest uppercase py-4 rounded-full hover:bg-[#b8995a] transition-colors disabled:opacity-50"
            data-testid="login-submit"
          >
            {submitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
